/**
 * Plan v2 Phase 5.1 / 5.3 / 5.4 - one-shot URL bootstrap parser for the
 * Dashboard. Reads `section`, `isRefundCandidate`, `txId` exactly once on
 * mount.
 *
 * Validation rules:
 *  - `section`             : only `'transactions'` is accepted (others ignored)
 *  - `isRefundCandidate`   : only `'true'` | `'false'` (others ignored)
 *  - `txId`                : positive integer (1-12 digits, no leading zero)
 *  - any other / missing values silently omitted (no error toast - Phase 5.4)
 *
 * Lifted out of Dashboard.tsx so it can be unit-tested directly without
 * mounting the entire dashboard tree.
 */

export interface UrlBootstrap {
  section?: 'transactions';
  isRefundCandidate?: 'true' | 'false';
  txId?: string;
  bankAccountIds?: string[];
}

export function parseUrlBootstrap(search?: string): UrlBootstrap {
  const source =
    search ?? (typeof window === 'undefined' ? '' : window.location.search);
  const params = new URLSearchParams(source);
  const result: UrlBootstrap = {};
  const sectionRaw = params.get('section');
  if (sectionRaw === 'transactions') {
    result.section = 'transactions';
  }
  const irc = params.get('isRefundCandidate');
  if (irc === 'true' || irc === 'false') {
    result.isRefundCandidate = irc;
  }
  const txIdRaw = params.get('txId');
  if (txIdRaw && /^[1-9][0-9]{0,11}$/.test(txIdRaw)) {
    result.txId = txIdRaw;
  }
  const bankAccountIdsRaw = params.get('bankAccountIds');
  if (bankAccountIdsRaw) {
    const bankAccountIds = normalizeIdList(bankAccountIdsRaw);
    if (bankAccountIds.length > 0) {
      result.bankAccountIds = bankAccountIds;
    }
  }
  return result;
}

function normalizeIdList(raw: string): string[] {
  return Array.from(
    new Set(
      raw
        .split(',')
        .map((value) => value.trim())
        .filter((value) => value.length > 0)
    )
  );
}
